import { useEffect, useState } from "react";
import { Icons } from "../components/Icons.js";
import { api, type DayNoteItem, type StandaloneNoteItem } from "../lib/api.js";
import { fmtLocalDate, fmtDate } from "../lib/format.js";
import type { EditTarget } from "./types.js";

type Filter = "tous" | "jour" | "autonome";

/**
 * Liste des enregistrements (§9) : notes du jour (note cumulée) et notes indépendantes. Chaque élément
 * ouvre la fenêtre d'édition. Les notes peuvent être envoyées à la corbeille.
 */
export function NotesView({ onOpen, reloadToken }: { onOpen: (t: EditTarget) => void; reloadToken: number }) {
  const [filter, setFilter] = useState<Filter>("tous");
  const [days, setDays] = useState<DayNoteItem[]>([]);
  const [notes, setNotes] = useState<StandaloneNoteItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([api.listDayNotes(), api.listStandaloneNotes()])
      .then(([d, n]) => {
        if (cancelled) return;
        setDays(d);
        setNotes(n);
      })
      .catch(() => undefined)
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [reloadToken, tick]);

  const removeNote = async (id: string) => {
    await api.deleteStandaloneNote(id).catch(() => undefined);
    setTick((t) => t + 1);
  };

  /**
   * Suppression d'une journée : elle est DÉFINITIVE côté serveur (la note du jour n'a pas de
   * corbeille, contrairement aux notes indépendantes), et elle emporte tous les enregistrements du
   * jour. On demande donc une confirmation : le premier appui arme la ligne, le second supprime.
   */
  const [confirmDay, setConfirmDay] = useState<string | null>(null);
  const removeDay = async (id: string) => {
    if (confirmDay !== id) {
      setConfirmDay(id);
      window.setTimeout(() => setConfirmDay((c) => (c === id ? null : c)), 5000);
      return;
    }
    setConfirmDay(null);
    await api.deleteDayNote(id).catch(() => undefined);
    setTick((t) => t + 1);
  };

  const showDays = filter === "tous" || filter === "jour";
  const showNotes = filter === "tous" || filter === "autonome";
  const empty = (!showDays || days.length === 0) && (!showNotes || notes.length === 0);

  return (
    <>
      <div className="filterbar">
        {([["tous", "Toutes"], ["jour", "Par jour"], ["autonome", "Indépendantes"]] as [Filter, string][]).map(([k, l]) => (
          <button key={k} className={"chip" + (filter === k ? " chip-on" : "")} onClick={() => setFilter(k)}>
            {l}
          </button>
        ))}
      </div>
      {loading ? (
        <div className="listspinner"><div className="spinner" /></div>
      ) : empty ? (
        <div className="empty">
          <div className="empty-ic"><Icons.Mic size={30} /></div>
          <div className="empty-tt">Aucun enregistrement</div>
          <div className="empty-sub">Vos notes du jour et vos notes apparaîtront ici.</div>
        </div>
      ) : (
        <div className="notelist">
          {showDays &&
            days.map((d) => (
              <div key={d.id} className="noterow" onClick={() => onOpen({ kind: "day", id: d.id, label: `Note du ${fmtLocalDate(d.localDate)}` })}>
                <div className="ntype ntype-voice"><Icons.Doc size={19} /></div>
                <div className="nmain">
                  <div className="ntitle">{fmtLocalDate(d.localDate)}</div>
                  <div className="nmeta">
                    <span>
                      {confirmDay === d.id ? "Appuyez à nouveau : suppression définitive" : `Note du jour · semaine ${d.isoWeek}`}
                    </span>
                  </div>
                </div>
                <div className="nactions" onClick={(e) => e.stopPropagation()}>
                  <button
                    className={"ntrash" + (confirmDay === d.id ? " ntrash-armed" : "")}
                    onClick={() => removeDay(d.id)}
                    aria-label={confirmDay === d.id ? "Confirmer la suppression définitive" : "Supprimer la journée"}
                    title={confirmDay === d.id ? "Confirmer : suppression définitive" : "Supprimer la journée (définitif)"}
                  >
                    <Icons.Trash size={18} />
                  </button>
                </div>
              </div>
            ))}
          {showNotes &&
            notes.map((n) => (
              <div key={n.id} className="noterow" onClick={() => onOpen({ kind: "standalone", id: n.id, label: n.title ?? "Note" })}>
                <div className="ntype ntype-text"><Icons.Mic size={19} /></div>
                <div className="nmain">
                  <div className="ntitle">{n.title ?? "Note"}</div>
                  <div className="nmeta">
                    <span>{fmtDate(new Date(n.occurredAt).getTime())}</span>
                    <span className="ndot">·</span>
                    <span>{n.hasTranscript ? "note" : "sans transcription"}</span>
                  </div>
                </div>
                <div className="nactions" onClick={(e) => e.stopPropagation()}>
                  <button className="ntrash" onClick={() => removeNote(n.id)} aria-label="Mettre à la corbeille">
                    <Icons.Trash size={18} />
                  </button>
                </div>
              </div>
            ))}
        </div>
      )}
    </>
  );
}
